// UserInvestmentDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { 
    User, 
    Mail,
    Calendar,
    Shield,
    Circle,
    DollarSign,
    Building2
} from 'lucide-react';
import { useParams } from "react-router";
import './UserInvestmentDetailPage.css';
import { formatCurrency, formatDateIntl } from "../../util/currency_lookup";
import { Loader } from '../../components/loader';
import {StatusDetailCard} from '../../components/StatusUpdate';
import {ConfirmationDialog} from '../../components/ConfirmDialog';

const UserInvestmentDetailPage = () => {
  const [user, setUser] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState({
    user: true,
    investments: true
  });
  const [error, setError] = useState(null);
  const { id } = useParams();
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleStatusToggle = (newStatus) => {
    setPendingStatus(newStatus);
    setIsConfirmOpen(true);
  };

  const confirmStatusUpdate = async () => {
    await updateUserStatus(pendingStatus);
    setIsConfirmOpen(false);
  };

  useEffect(() => {
    fetchUser();
    fetchInvestments();
  }, [id]);

  const updateUserStatus = async (newStatus) => {
    setIsUpdating(true);
    setUpdateError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/users/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: newStatus })
      });

      if (!response.ok) {
        throw new Error('Failed to update user status');
      }

      // Update local state
      setUser(prev => ({
        ...prev,
        is_active: newStatus
      }));
      setShowConfirmModal(false);

    } catch (err) {
      setUpdateError(err.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStatusClick = (newStatus) => {
    setPendingStatus(newStatus);
    setShowConfirmModal(true);
  };

  const fetchUser = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/users-by-id/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }

      const data = await response.json();
      setUser(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, user: false }));
    }
  };

  const fetchInvestments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/users/investment/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch investments');
      }

      const data = await response.json();
      setInvestments(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(prev => ({ ...prev, investments: false }));
    }
  };

  const isLoading = Object.values(loading).some(status => status);

  if (isLoading) {
    return <Loader loading={true} />;
  }

  if (error) return <div className="error">{error}</div>;
  if (!user) return <div className="error">No user found</div>;

  return (
    <div className="user-investment-detail">
      {/* Header */}
      <div className="header">
        <div className="header-content">
          <h1>User Investment Details</h1>
          <div className="user-email">{user.email}</div>
        </div>
      </div>

      {/* Basic User Information */}
      <div className="overview">
        <div className="overview-card">
          <div className="overview-title">User Information</div>
          <div className="details-grid">
            <div className="detail-card">
              <div className="detail-card-content">
                <User className="detail-icon" />
                <div>
                  <div className="detail-label">Name</div>
                  <div className="detail-value">
                    {user.first_name} {user.last_name}
                  </div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <Mail className="detail-icon" />
                <div>
                  <div className="detail-label">Email</div>
                  <div className="detail-value">{user.email}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <Shield className="detail-icon" />
                <div>
                  <div className="detail-label">Role</div>
                  <div className="detail-value">{user.role}</div>
                </div>
              </div>
            </div>
            <div className="detail-card">
              <div className="detail-card-content">
                <Calendar className="detail-icon" />
                <div>
                  <div className="detail-label">Created At</div>
                  <div className="detail-value">{formatDateIntl(user.created_at)}</div>
                </div>
              </div>
            </div>
            <StatusDetailCard
                handleStatusClick={handleStatusClick}
                is_active={user.is_active}
                isUpdating={isUpdating}
                updateError={updateError}
            />
          </div>
        </div>
      </div>

      {/* Investments Table */}
      <div className="investments-section">
        <div className="section-header">
          <h2>
            <Building2 className="section-icon" />
            Fund Investments
          </h2>
          <div className="total-investments">
            Total Funds: {investments.length}
          </div>
        </div>

        <div className="table-container">
          <table className="investments-table">
            <thead>
              <tr>
                <th>Fund Name</th>
                <th>Symbol</th>
                <th>Net Investment</th>
                <th>Total Investment</th>
                <th>Total Redemption</th>
                <th>Transactions</th>
                <th>Largest Investment</th>
              </tr>
            </thead>
            <tbody>
              {investments.map((investment) => (
                <tr key={investment.fund_id}>
                  <td>
                    <div className="fund-name-cell">
                      <Building2 className="fund-icon" />
                      <span>{investment.fund_name}</span>
                    </div>
                  </td>
                  <td>{investment.symbol}</td>
                  <td className={investment.net_investment >= 0 ? 'positive' : 'negative'}>
                    {formatCurrency(investment.net_investment, investment.currency_code)}
                  </td>
                  <td>
                    {formatCurrency(investment.total_investments, investment.currency_code)}
                  </td>
                  <td>
                    {formatCurrency(investment.total_redemptions, investment.currency_code)}
                  </td>
                  <td>{investment.total_transactions}</td>
                  <td>
                    {formatCurrency(investment.largest_investment, investment.currency_code)}
                  </td>
                </tr>
              ))}
              {investments.length === 0 && (
                <tr>
                  <td colSpan="7" className="no-data">
                    No investments found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <ConfirmationDialog 
            showConfirmModal={showConfirmModal}
            updateUserStatus={updateUserStatus}
            setShowConfirmModal={setShowConfirmModal}
            pendingStatus={pendingStatus}
            isUpdating={isUpdating}
          />
        </div>
      </div>
    </div>
  );
};

export default UserInvestmentDetailPage;
